import React, { useState } from 'react';
import { useQuery } from 'react-query';
import styled from 'styled-components';
import { adminAPI } from '../services/adminApi';
import { adminSeasonsAPI, adminEventTournamentsAPI } from '../services/adminApi';

const PromoCodesContainer = styled.div`
  padding: 1rem;
  
  @media (max-width: 768px) {
    padding: 0.5rem;
  }
`;

const SummaryGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1.5rem;
  margin-bottom: 2rem;
  
  @media (max-width: 768px) {
    grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
    gap: 1rem;
    margin-bottom: 1.5rem;
  }
`;

const SummaryCard = styled.div`
  background: ${props => props.$color || 'linear-gradient(135deg, #043663 0%, #032a4a 100%)'};
  color: white;
  padding: 1.5rem;
  border-radius: 12px;
  text-align: center;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
  
  @media (max-width: 768px) {
    padding: 1rem;
    border-radius: 8px;
  }
  
  .number {
    font-size: 2.5rem;
    font-weight: 700;
    margin-bottom: 0.5rem;
    
    @media (max-width: 768px) {
      font-size: 2rem;
    }
  }
  
  .label {
    font-size: 0.875rem;
    opacity: 0.9;
    
    @media (max-width: 768px) {
      font-size: 0.8rem;
    }
  }
  
  .percentage {
    font-size: 0.75rem;
    margin-top: 0.5rem;
    opacity: 0.8;
    
    @media (max-width: 768px) {
      font-size: 0.7rem;
    }
  }
`;

const Section = styled.div`
  background: white;
  border-radius: 12px;
  padding: 1.5rem;
  margin-bottom: 1.5rem;
  box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1);
  
  @media (max-width: 768px) {
    padding: 1rem;
    border-radius: 8px;
    margin-bottom: 1rem;
  }
`;

const SectionTitle = styled.h3`
  font-size: 1.25rem;
  font-weight: 600;
  color: #1f2937;
  margin-bottom: 1rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  
  @media (max-width: 768px) {
    font-size: 1.1rem;
    margin-bottom: 0.75rem;
    flex-wrap: wrap;
  }
`;

const TableWrapper = styled.div`
  width: 100%;
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
  border-radius: 8px;
  
  @media (max-width: 768px) {
    margin: 0 -1rem;
    padding: 0 1rem;
  }
  
  &::-webkit-scrollbar {
    height: 8px;
  }
  
  &::-webkit-scrollbar-track {
    background: #f1f1f1;
    border-radius: 10px;
  }
  
  &::-webkit-scrollbar-thumb {
    background: #888;
    border-radius: 10px;
  }
  
  &::-webkit-scrollbar-thumb:hover {
    background: #555;
  }
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  min-width: 600px;
  
  @media (max-width: 768px) {
    font-size: 0.8rem;
  }
  
  thead {
    background: #f9fafb;
  }
  
  th {
    padding: 0.75rem 1rem;
    text-align: left;
    font-size: 0.875rem;
    font-weight: 600;
    color: #374151;
    border-bottom: 2px solid #e5e7eb;
    white-space: nowrap;
    
    @media (max-width: 768px) {
      padding: 0.75rem 0.5rem;
      font-size: 0.75rem;
    }
  }
  
  td {
    padding: 0.75rem 1rem;
    font-size: 0.875rem;
    color: #6b7280;
    border-bottom: 1px solid #f3f4f6;
    
    @media (max-width: 768px) {
      padding: 0.75rem 0.5rem;
      font-size: 0.8rem;
    }
  }
  
  tbody tr:hover {
    background: #f9fafb;
  }
`;

const ReferralGroup = styled.div`
  background: #f9fafb;
  border-radius: 8px;
  padding: 1rem;
  margin-bottom: 1rem;
  border-left: 4px solid #043663;
  
  @media (max-width: 768px) {
    padding: 0.75rem;
    margin-bottom: 0.75rem;
  }
`;

const ReferralHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.75rem;
  
  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 0.75rem;
    margin-bottom: 0.5rem;
  }
  
  .team-name {
    font-size: 1.1rem;
    font-weight: 600;
    color: #1f2937;
    
    @media (max-width: 768px) {
      font-size: 1rem;
    }
  }
  
  .stats {
    display: flex;
    gap: 1rem;
    font-size: 0.875rem;
    color: #6b7280;
    
    @media (max-width: 768px) {
      flex-wrap: wrap;
      gap: 0.5rem;
      width: 100%;
    }
    
    .stat {
      background: white;
      padding: 0.25rem 0.75rem;
      border-radius: 6px;
      font-weight: 500;
      
      @media (max-width: 768px) {
        padding: 0.2rem 0.5rem;
        font-size: 0.8rem;
      }
    }
  }
`;

const ReferredTeamsList = styled.div`
  display: grid;
  gap: 0.5rem;
`;

const ReferredTeamItem = styled.div`
  background: white;
  padding: 0.75rem;
  border-radius: 6px;
  display: grid;
  grid-template-columns: 2fr 2fr 1fr 1fr;
  gap: 1rem;
  align-items: center;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 0.5rem;
    padding: 0.5rem;
  }
  
  .team-name {
    font-weight: 500;
    color: #1f2937;
    
    @media (max-width: 768px) {
      font-size: 0.9rem;
    }
  }
  
  .email {
    color: #6b7280;
    font-size: 0.875rem;
    
    @media (max-width: 768px) {
      font-size: 0.8rem;
    }
  }
  
  .canton {
    text-align: center;
    padding: 0.25rem 0.5rem;
    background: #e0e7ff;
    color: #3730a3;
    border-radius: 4px;
    font-size: 0.75rem;
    font-weight: 600;
    
    @media (max-width: 768px) {
      text-align: left;
      display: inline-block;
      width: fit-content;
    }
  }
  
  .discount {
    text-align: right;
    color: #10b981;
    font-weight: 600;
    
    @media (max-width: 768px) {
      text-align: left;
    }
  }
`;

const LoadingMessage = styled.div`
  text-align: center;
  padding: 2rem;
  color: #6b7280;
`;

const ErrorMessage = styled.div`
  background: #fee2e2;
  color: #dc2626;
  padding: 1rem;
  border-radius: 8px;
  text-align: center;
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 2rem;
  color: #9ca3af;
  
  .icon {
    font-size: 3rem;
    margin-bottom: 1rem;
  }
`;

const Tabs = styled.div`
  display: flex;
  gap: 0.5rem;
  margin-bottom: 1.5rem;
  border-bottom: 2px solid #e5e7eb;
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
  
  @media (max-width: 768px) {
    margin-bottom: 1rem;
    gap: 0.25rem;
  }
  
  &::-webkit-scrollbar {
    height: 4px;
  }
  
  &::-webkit-scrollbar-track {
    background: #f1f1f1;
  }
  
  &::-webkit-scrollbar-thumb {
    background: #888;
    border-radius: 10px;
  }
`;

const Tab = styled.button`
  padding: 0.75rem 1.5rem;
  border: none;
  background: none;
  color: ${props => props.$active ? '#043663' : '#6b7280'};
  font-weight: ${props => props.$active ? '600' : '500'};
  border-bottom: 2px solid ${props => props.$active ? '#043663' : 'transparent'};
  margin-bottom: -2px;
  cursor: pointer;
  transition: all 0.2s ease;
  white-space: nowrap;
  flex-shrink: 0;
  
  &:hover {
    color: #043663;
  }
  
  @media (max-width: 768px) {
    padding: 0.6rem 1rem;
    font-size: 0.85rem;
  }
`;

const FilterContainer = styled.div`
  display: flex;
  gap: 1rem;
  margin-bottom: 1.5rem;
  align-items: center;
  flex-wrap: wrap;
  
  @media (max-width: 768px) {
    flex-direction: column;
    align-items: stretch;
  }
`;

const FilterLabel = styled.label`
  font-size: 0.875rem;
  font-weight: 600;
  color: #374151;
  white-space: nowrap;
  
  @media (max-width: 768px) {
    font-size: 0.8rem;
  }
`;

const FilterSelect = styled.select`
  padding: 0.75rem 1rem;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-size: 0.875rem;
  background: white;
  min-width: 200px;
  
  &:focus {
    outline: none;
    border-color: #043663;
    box-shadow: 0 0 0 3px rgba(4, 54, 99, 0.1);
  }
  
  @media (max-width: 768px) {
    width: 100%;
    min-width: 100%;
  }
`;

const AdminPromoCodesSection = () => {
  const [activeTab, setActiveTab] = useState('summary');
  const [selectedCampaign, setSelectedCampaign] = useState('');
  const [selectedSeason, setSelectedSeason] = useState('');

  const { data: campaignsData } = useQuery(
    'admin-campaigns-promo-stats',
    () => adminEventTournamentsAPI.getAll(),
    { retry: false }
  );

  const { data: linkedSeasonsData } = useQuery(
    ['admin-campaign-seasons-promo', selectedCampaign],
    () => adminEventTournamentsAPI.getLinkedSeasons(selectedCampaign),
    {
      enabled: Boolean(selectedCampaign),
      retry: false,
      onSuccess: (data) => {
        const raw = data?.data ?? data ?? [];
        const seasons = (Array.isArray(raw) ? raw : []).map((s) => ({
          id: s.season_id || s.id,
          name: s.name || s.season_name,
          status: s.status,
        }));
        if (!seasons.length) return;
        if (seasons.some((s) => String(s.id) === String(selectedSeason))) return;
        const open = seasons.find((s) => s.status === 'registration_open');
        const active = seasons.find((s) => s.status === 'active');
        setSelectedSeason(open ? open.id : active ? active.id : seasons[0].id);
      },
    }
  );

  // Fallback: all seasons when no campaign selected
  const { data: seasonsData } = useQuery(
    'admin-seasons-promo',
    () => adminSeasonsAPI.getAll(),
    {
      retry: false,
      enabled: !selectedCampaign,
      onSuccess: (data) => {
        if (data.success && data.data.length > 0 && !selectedSeason) {
          const open = data.data.find((s) => s.status === 'registration_open');
          const active = data.data.find((s) => s.status === 'active');
          setSelectedSeason(open ? open.id : active ? active.id : data.data[0].id);
        }
      },
      onError: (error) => {
        console.error('Error fetching seasons:', error);
      },
    }
  );

  const linkedRaw = linkedSeasonsData?.data ?? linkedSeasonsData ?? [];
  const campaignSeasons = (Array.isArray(linkedRaw) ? linkedRaw : []).map((s) => ({
    id: s.season_id || s.id,
    name: s.name || s.season_name,
    status: s.status,
  }));
  const seasonOptions = selectedCampaign ? campaignSeasons : seasonsData?.data || [];

  const { data, isLoading, error } = useQuery(
    ['promoStatistics', selectedSeason, selectedCampaign],
    async () => {
      const params = new URLSearchParams();
      if (selectedSeason) params.set('season_id', selectedSeason);
      if (selectedCampaign) params.set('campaign_id', selectedCampaign);
      const qs = params.toString() ? `?${params.toString()}` : '';
      const response = await adminAPI.get(`/admin/promo-statistics${qs}`);
      return response;
    },
    {
      refetchInterval: 30000,
      enabled: Boolean(selectedSeason),
    }
  );

  if (isLoading && selectedSeason) {
    return <LoadingMessage>Loading promo code statistics...</LoadingMessage>;
  }

  if (error) {
    return <ErrorMessage>Error loading statistics: {error.message}</ErrorMessage>;
  }

  const stats = data?.data || {};
  const summary = stats.summary || {};
  const promoCodeList = stats.promoCodeList || [];
  const promoCodeCounts = summary.promoCodeCounts || [];
  const teamsByCode = stats.teamsByCode || {};
  const noPromoTeams = stats.noPromoTeams || [];
  const loyaltyTeams = stats.loyaltyTeams || [];
  const liveTeamReferralTeams = stats.liveTeamReferralTeams || stats.teamReferralTeams || [];
  const unlinkedTeamPromos = stats.unlinkedTeamPromos || [];
  const loyaltyReferralsByTeam = stats.loyaltyReferralsByTeam || [];
  const liveReferralsByTeam = stats.liveReferralsByTeam || [];
  const loyaltyCount = summary.loyaltyCount ?? loyaltyTeams.length;
  const teamReferralCount = summary.teamReferralCount ?? liveTeamReferralTeams.length;
  const unlinkedPromoCount = summary.unlinkedPromoCount ?? unlinkedTeamPromos.length;
  const officialPromoCount =
    summary.officialPromoCount ??
    summary.officialPromoTeams ??
    promoCodeCounts.reduce((s, row) => s + (Number(row.count) || 0), 0);

  const groupPromoTeams = (teams, defaultLabel) => {
    const grouped = teams.reduce((acc, team) => {
      const key =
        team.referring_team_id ||
        (team.promo_code_used ? `promo:${team.promo_code_used}` : '__no_record__');
      let label = team.referring_team_name;
      if (!label && team.promo_code_used) {
        label =
          defaultLabel === 'Team referral'
            ? `Code entered (referring team not linked): ${team.promo_code_used}`
            : `${defaultLabel}: ${team.promo_code_used}`;
      }
      if (!label) label = `${defaultLabel} record missing`;
      if (!acc[key]) acc[key] = { referring_team_name: label, referred_teams: [] };
      acc[key].referred_teams.push(team);
      return acc;
    }, {});
    return Object.values(grouped).sort((a, b) => b.referred_teams.length - a.referred_teams.length);
  };

  const loyaltyGroups = groupPromoTeams(loyaltyTeams, 'Loyalty');
  const teamReferralGroups = groupPromoTeams(liveTeamReferralTeams, 'Team referral');
  const unlinkedGroups = groupPromoTeams(unlinkedTeamPromos, 'Unlinked code');

  const getPercentage = (count) => {
    if (summary.totalTeams === 0) return '0%';
    return `${((count / summary.totalTeams) * 100).toFixed(1)}%`;
  };

  const CARD_COLORS = [
    'linear-gradient(135deg, #10b981 0%, #059669 100%)',
    'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
    'linear-gradient(135deg, #06b6d4 0%, #0891b2 100%)',
    'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
    'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)',
    'linear-gradient(135deg, #ec4899 0%, #db2777 100%)',
    'linear-gradient(135deg, #14b8a6 0%, #0d9488 100%)',
    'linear-gradient(135deg, #f97316 0%, #ea580c 100%)',
  ];

  const campaigns = campaignsData?.data || campaignsData || [];

  return (
    <PromoCodesContainer>
      <FilterContainer>
        <FilterLabel>Season:</FilterLabel>
        <FilterSelect
          value={selectedCampaign}
          onChange={(e) => {
            setSelectedCampaign(e.target.value);
            setActiveTab('summary');
          }}
          style={{
            border: selectedCampaign ? '2px solid #043663' : '1px solid #d1d5db',
            fontWeight: selectedCampaign ? 600 : 400,
          }}
        >
          <option value="">All seasons (pick a phase below)</option>
          {(Array.isArray(campaigns) ? campaigns : []).map((c) => (
            <option key={c.id} value={c.id}>
              {c.title || c.name || c.id}
            </option>
          ))}
        </FilterSelect>
        <FilterLabel>Phase:</FilterLabel>
        <FilterSelect
          value={selectedSeason}
          onChange={(e) => setSelectedSeason(e.target.value)}
          style={{
            border: selectedSeason ? '2px solid #043663' : '1px solid #d1d5db',
            fontWeight: selectedSeason ? 600 : 400
          }}
        >
          <option value="">
            All phases (lifetime — not recommended for season comparisons)
          </option>
          {seasonOptions.map((season) => (
            <option key={season.id} value={season.id}>
              {season.name}{' '}
              {season.status === 'active'
                ? '(Active)'
                : season.status === 'registration_open'
                  ? '(Registration open)'
                : season.status === 'completed'
                  ? '(Completed)'
                  : ''}
            </option>
          ))}
        </FilterSelect>
      </FilterContainer>
      <p style={{ fontSize: '0.875rem', color: '#6b7280', marginTop: '-0.75rem', marginBottom: '1.25rem', maxWidth: '52rem' }}>
        Pick a <strong>season</strong> then a <strong>phase</strong> for that season. Totals are scoped to that
        phase only (historical seasons stay unchanged). <strong>Official</strong> = admin promo codes;{' '}
        <strong>Loyalty</strong> = prior completed-season team names (once per org);{' '}
        <strong>Team referral</strong> = paid team names in this live phase (unlimited uses).
        {!selectedSeason && (
          <> <strong>Select a phase</strong> below — &quot;All phases (lifetime)&quot; mixes every phase.</>
        )}
      </p>

      {!selectedSeason ? (
        <LoadingMessage>Select a phase to load statistics for that phase only.</LoadingMessage>
      ) : (
      <>
      <SummaryGrid>
        <SummaryCard $color="linear-gradient(135deg, #043663 0%, #032a4a 100%)">
          <div className="number">{summary.totalTeams || 0}</div>
          <div className="label">
            {selectedSeason ? 'Checkout registrations (promo cohort)' : 'Total paid teams (all phases)'}
          </div>
          {selectedSeason &&
            summary.totalPaidRegistrationsAllSources != null &&
            summary.totalPaidRegistrationsAllSources > (summary.totalTeams || 0) && (
              <div
                className="percentage"
                style={{ marginTop: '0.35rem', fontSize: '0.8rem', opacity: 0.95, lineHeight: 1.35 }}
              >
                +{summary.totalPaidRegistrationsAllSources - (summary.totalTeams || 0)} other paid in phase
                <br />
                (carried / auto-registered, not in promo totals)
              </div>
            )}
        </SummaryCard>
        <SummaryCard $color="linear-gradient(135deg, #10b981 0%, #059669 100%)">
          <div className="number">{officialPromoCount}</div>
          <div className="label">Official promo uses</div>
          <div className="percentage">{getPercentage(officialPromoCount)}</div>
        </SummaryCard>
        <SummaryCard $color="linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)">
          <div className="number">{loyaltyCount}</div>
          <div className="label">Loyalty uses</div>
          <div className="percentage">{getPercentage(loyaltyCount)}</div>
        </SummaryCard>
        <SummaryCard $color="linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)">
          <div className="number">{teamReferralCount}</div>
          <div className="label">Team referral uses</div>
          <div className="percentage">{getPercentage(teamReferralCount)}</div>
        </SummaryCard>
        <SummaryCard $color="linear-gradient(135deg, #6b7280 0%, #4b5563 100%)">
          <div className="number">{summary.noPromoCount || 0}</div>
          <div className="label">No Promo Code</div>
          <div className="percentage">{getPercentage(summary.noPromoCount)}</div>
        </SummaryCard>
        {promoCodeCounts.map(({ code, count }, idx) => (
          <SummaryCard key={code} $color={CARD_COLORS[idx % CARD_COLORS.length]}>
            <div className="number">{count}</div>
            <div className="label">Official: {code}</div>
            <div className="percentage">{getPercentage(count)}</div>
          </SummaryCard>
        ))}
      </SummaryGrid>

      <Tabs>
        <Tab $active={activeTab === 'summary'} onClick={() => setActiveTab('summary')}>
          Summary
        </Tab>
        <Tab $active={activeTab === 'loyalty'} onClick={() => setActiveTab('loyalty')}>
          Loyalty ({loyaltyCount})
        </Tab>
        <Tab $active={activeTab === 'team-referrals'} onClick={() => setActiveTab('team-referrals')}>
          Team referral ({teamReferralCount})
        </Tab>
        {unlinkedPromoCount > 0 ? (
          <Tab $active={activeTab === 'unlinked'} onClick={() => setActiveTab('unlinked')}>
            Unlinked ({unlinkedPromoCount})
          </Tab>
        ) : null}
        {promoCodeCounts.map(({ code }) => (
          <Tab key={code} $active={activeTab === `code-${code}`} onClick={() => setActiveTab(`code-${code}`)}>
            Official: {code} ({(teamsByCode[code] || []).length})
          </Tab>
        ))}
        <Tab $active={activeTab === 'no-promo'} onClick={() => setActiveTab('no-promo')}>
          No Promo ({noPromoTeams.length})
        </Tab>
      </Tabs>

      {activeTab === 'summary' && (
        <>
          <Section>
            <SectionTitle>Top loyalty referring teams (this phase)</SectionTitle>
            {loyaltyReferralsByTeam.length > 0 ? (
              <Table>
                <thead>
                  <tr>
                    <th>Rank</th>
                    <th>Loyalty team name</th>
                    <th>Uses</th>
                    <th>Total Discount Given</th>
                  </tr>
                </thead>
                <tbody>
                  {loyaltyReferralsByTeam.slice(0, 10).map((team, index) => (
                    <tr key={team.referring_team_id}>
                      <td>#{index + 1}</td>
                      <td><strong>{team.referring_team_name}</strong></td>
                      <td>{team.referral_count} team{team.referral_count === 1 ? '' : 's'}</td>
                      <td>CHF {team.total_discount_given}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            ) : (
              <EmptyState>
                <div className="icon">📭</div>
                <p>No loyalty uses in this scope yet</p>
              </EmptyState>
            )}
          </Section>
          <Section>
            <SectionTitle>Top team referral names (this phase — unlimited uses)</SectionTitle>
            {liveReferralsByTeam.length > 0 ? (
              <Table>
                <thead>
                  <tr>
                    <th>Rank</th>
                    <th>Referring team (live phase)</th>
                    <th>Uses</th>
                    <th>Total Discount Given</th>
                  </tr>
                </thead>
                <tbody>
                  {liveReferralsByTeam.slice(0, 10).map((team, index) => (
                    <tr key={team.referring_team_id}>
                      <td>#{index + 1}</td>
                      <td><strong>{team.referring_team_name}</strong></td>
                      <td>{team.referral_count} team{team.referral_count === 1 ? '' : 's'}</td>
                      <td>CHF {team.total_discount_given}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            ) : (
              <EmptyState>
                <div className="icon">📭</div>
                <p>No team referral uses in this scope yet</p>
              </EmptyState>
            )}
          </Section>
        </>
      )}

      {activeTab === 'loyalty' && (
        <Section>
          <SectionTitle>Loyalty uses – by prior team name</SectionTitle>
          <p style={{ marginBottom: '1rem', color: '#666' }}>
            Team names from a <strong>completed</strong> past phase/campaign. Each loyalty name can be used once
            per org on this phase (creator + partner on the same new team share one use).
          </p>
          {loyaltyGroups.length > 0 ? (
            loyaltyGroups.map((group) => (
              <ReferralGroup key={group.referring_team_name}>
                <ReferralHeader>
                  <div className="team-name">🏆 {group.referring_team_name}</div>
                  <div className="stats">
                    <span className="stat">{group.referred_teams.length} use{group.referred_teams.length !== 1 ? 's' : ''}</span>
                    <span className="stat">CHF {group.referred_teams.reduce((s, t) => s + (t.discount_amount || 0), 0)} given</span>
                  </div>
                </ReferralHeader>
                <ReferredTeamsList>
                  {group.referred_teams.map((referred) => (
                    <ReferredTeamItem key={referred.id}>
                      <div className="team-name">{referred.team_name}</div>
                      <div className="email">{referred.email}</div>
                      <div className="canton">{referred.canton}</div>
                      {referred.promo_code_used ? (
                        <div className="canton" style={{ fontSize: '0.8rem', color: '#64748b' }}>
                          Code: {referred.promo_code_used}
                        </div>
                      ) : null}
                      <div className="discount">{referred.discount_amount != null ? `-CHF ${referred.discount_amount}` : '—'}</div>
                    </ReferredTeamItem>
                  ))}
                </ReferredTeamsList>
              </ReferralGroup>
            ))
          ) : (
            <EmptyState>
              <div className="icon">📭</div>
              <p>No loyalty uses in this scope yet</p>
            </EmptyState>
          )}
        </Section>
      )}

      {activeTab === 'team-referrals' && (
        <Section>
          <SectionTitle>Team referral uses – by live phase team name</SectionTitle>
          <p style={{ marginBottom: '1rem', color: '#666' }}>
            Team names from <strong>paid teams in this live phase</strong>. Unlimited uses — many teams can enter
            the same referring team name.
          </p>
          {teamReferralGroups.length > 0 ? (
            teamReferralGroups.map((group) => (
              <ReferralGroup key={group.referring_team_name}>
                <ReferralHeader>
                  <div className="team-name">🤝 {group.referring_team_name}</div>
                  <div className="stats">
                    <span className="stat">{group.referred_teams.length} use{group.referred_teams.length !== 1 ? 's' : ''}</span>
                    <span className="stat">CHF {group.referred_teams.reduce((s, t) => s + (t.discount_amount || 0), 0)} given</span>
                  </div>
                </ReferralHeader>
                <ReferredTeamsList>
                  {group.referred_teams.map((referred) => (
                    <ReferredTeamItem key={referred.id}>
                      <div className="team-name">{referred.team_name}</div>
                      <div className="email">{referred.email}</div>
                      <div className="canton">{referred.canton}</div>
                      {referred.promo_code_used ? (
                        <div className="canton" style={{ fontSize: '0.8rem', color: '#64748b' }}>
                          Code: {referred.promo_code_used}
                        </div>
                      ) : null}
                      <div className="discount">{referred.discount_amount != null ? `-CHF ${referred.discount_amount}` : '—'}</div>
                    </ReferredTeamItem>
                  ))}
                </ReferredTeamsList>
              </ReferralGroup>
            ))
          ) : (
            <EmptyState>
              <div className="icon">📭</div>
              <p>No team referral uses in this scope yet</p>
            </EmptyState>
          )}
        </Section>
      )}

      {activeTab === 'unlinked' && (
        <Section>
          <SectionTitle>Unlinked team-name codes – needs review</SectionTitle>
          <p style={{ marginBottom: '1rem', color: '#666' }}>
            These checkouts saved a promo string that does <strong>not</strong> match any real loyalty or live
            referring team name, or the referring team was never recorded. Text like &quot;Your 2025–2026 team
            name&quot; is registration instruction copy — not a valid code. The player should enter an actual
            team name from a completed phase (loyalty) or a paid team in this live phase (team referral).
          </p>
          {unlinkedGroups.length > 0 ? (
            unlinkedGroups.map((group) => (
              <ReferralGroup key={group.referring_team_name}>
                <ReferralHeader>
                  <div className="team-name">⚠️ {group.referring_team_name}</div>
                  <div className="stats">
                    <span className="stat">{group.referred_teams.length} checkout{group.referred_teams.length !== 1 ? 's' : ''}</span>
                    <span className="stat">CHF {group.referred_teams.reduce((s, t) => s + (t.discount_amount || 0), 0)} given</span>
                  </div>
                </ReferralHeader>
                <ReferredTeamsList>
                  {group.referred_teams.map((referred) => (
                    <ReferredTeamItem key={referred.id}>
                      <div className="team-name">{referred.team_name}</div>
                      <div className="email">{referred.email}</div>
                      <div className="canton">{referred.canton}</div>
                      {referred.promo_code_used ? (
                        <div className="canton" style={{ fontSize: '0.8rem', color: '#64748b' }}>
                          Saved as: {referred.promo_code_used}
                        </div>
                      ) : null}
                      <div className="discount">{referred.discount_amount != null ? `-CHF ${referred.discount_amount}` : '—'}</div>
                    </ReferredTeamItem>
                  ))}
                </ReferredTeamsList>
              </ReferralGroup>
            ))
          ) : (
            <EmptyState>
              <div className="icon">✓</div>
              <p>No unlinked codes in this scope</p>
            </EmptyState>
          )}
        </Section>
      )}

      {activeTab.startsWith('code-') && (() => {
        const code = activeTab.replace(/^code-/, '');
        const codeTeams = teamsByCode[code] || [];
        return (
          <Section key={code}>
            <SectionTitle>🎟️ Teams Using {code}</SectionTitle>
            {codeTeams.length > 0 ? (
              <TableWrapper>
                <Table>
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>Team Name</th>
                      <th>Email</th>
                      <th>Canton</th>
                      <th>Registered</th>
                    </tr>
                  </thead>
                  <tbody>
                    {codeTeams.map((team, index) => (
                      <tr key={team.id}>
                        <td>{index + 1}</td>
                        <td><strong>{team.team_name}</strong></td>
                        <td>{team.email}</td>
                        <td>{team.canton}</td>
                        <td>{new Date(team.created_at).toLocaleDateString()}</td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </TableWrapper>
            ) : (
              <EmptyState>
                <div className="icon">📭</div>
                <p>No teams used {code} yet</p>
              </EmptyState>
            )}
          </Section>
        );
      })()}

      {activeTab === 'no-promo' && (
        <Section>
          <SectionTitle>❌ Teams Without Promo Code</SectionTitle>
          {noPromoTeams.length > 0 ? (
            <TableWrapper>
              <Table>
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Team Name</th>
                    <th>Email</th>
                    <th>Canton</th>
                    <th>Registered</th>
                  </tr>
                </thead>
                <tbody>
                  {noPromoTeams.map((team, index) => (
                    <tr key={team.id}>
                      <td>{index + 1}</td>
                      <td><strong>{team.team_name}</strong></td>
                      <td>{team.email}</td>
                      <td>{team.canton}</td>
                      <td>{new Date(team.created_at).toLocaleDateString()}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </TableWrapper>
          ) : (
            <EmptyState>
              <div className="icon">🎉</div>
              <p>All teams used a promo code!</p>
            </EmptyState>
          )}
        </Section>
      )}
      </>
      )}
    </PromoCodesContainer>
  );
};

export default AdminPromoCodesSection;

